import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line,
} from 'recharts';
import {
  LayoutDashboard, AlertTriangle, AlertCircle, Info, Wallet,
  Receipt, TrendingUp, CheckCircle2, ArrowRight, CalendarDays,
  Building2, Activity,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBRL = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);
const fmtPct = (n) => `${Math.round(n || 0)}%`;

const formatMonth = (key) => {
  if (!key) return '—';
  const [y, m] = key.split('-');
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${months[parseInt(m, 10) - 1]}/${y.slice(2)}`;
};

const formatDateBR = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('pt-BR');
  } catch (_e) {
    return iso;
  }
};

const ALERT_STYLES = {
  critical: {
    icon: AlertCircle,
    bg: 'bg-red-50 border-red-200',
    text: 'text-red-700',
    badge: 'bg-red-100 text-red-700 border-red-200',
    label: 'Crítico',
  },
  warning: {
    icon: AlertTriangle,
    bg: 'bg-amber-50 border-amber-200',
    text: 'text-amber-700',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
    label: 'Atenção',
  },
  info: {
    icon: Info,
    bg: 'bg-blue-50 border-blue-200',
    text: 'text-blue-700',
    badge: 'bg-blue-100 text-blue-700 border-blue-200',
    label: 'Aviso',
  },
};

const useFinancialDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/financeiro/dashboard`, {
          withCredentials: true,
        });
        if (mounted) setData(res.data);
      } catch (_err) {
        // silent
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);
  return { data, loading };
};

const KPICard = ({ icon: Icon, label, value, sub, accent = 'text-foreground' }) => (
  <Card>
    <CardContent className="pt-5">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold mt-1 truncate ${accent}`}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <Icon className="h-5 w-5 text-primary shrink-0" />
      </div>
    </CardContent>
  </Card>
);

const AlertCard = ({ alert }) => {
  const s = ALERT_STYLES[alert.level] || ALERT_STYLES.info;
  const Icon = s.icon;
  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border ${s.bg}`}
      data-testid={`alert-${alert.id}`}
    >
      <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${s.text}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className={`font-bold text-sm ${s.text}`}>{alert.title}</span>
          <Badge variant="outline" className={s.badge}>{s.label}</Badge>
        </div>
        <p className="text-sm text-foreground/80">{alert.message}</p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// DASHBOARD — executive view
// ---------------------------------------------------------------------------
export const FinanceiroDashboardPage = () => {
  const { data, loading } = useFinancialDashboard();

  if (loading || !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  const k = data.kpis;
  const pctAccent = k.pct_consumido > 100
    ? 'text-red-600'
    : k.pct_consumido > 85
    ? 'text-amber-600'
    : 'text-emerald-600';

  // Build pie chart data: areas with budget > 0
  const pieData = (data.by_area || [])
    .filter((a) => a.total_gasto > 0)
    .map((a) => ({
      name: a.area_name,
      value: a.total_gasto,
      color: a.area_cor || '#94a3b8',
    }));

  const hasData = pieData.length > 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="financeiro-dashboard-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <LayoutDashboard className="h-6 w-6 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight">Dashboard Financeiro</h1>
              <p className="text-muted-foreground mt-1">
                {data.plan?.name || 'Orçamento'} ·
                {' '}status: <span className="font-medium">{data.plan?.status || 'rascunho'}</span>
              </p>
            </div>
          </div>
          <Button asChild variant="outline" size="sm" data-testid="link-analise">
            <Link to="/financeiro/analise">
              Análise detalhada <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Link>
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPICard icon={Wallet} label="Orçamento total" value={fmtBRL(k.valor_total_orcamento)} />
          <KPICard
            icon={Receipt}
            label="Pago"
            value={fmtBRL(k.total_pago)}
            sub={`${k.pct_consumido}% do orçamento`}
            accent={pctAccent}
          />
          <KPICard icon={CheckCircle2} label="Saldo" value={fmtBRL(k.saldo)} accent="text-emerald-600" />
          <KPICard icon={TrendingUp} label="Planejado" value={fmtBRL(k.total_planejado)} sub="ainda não pago" />
          <KPICard
            icon={AlertTriangle}
            label="Alertas ativos"
            value={k.count_alertas}
            sub={k.count_alertas > 0 ? 'verifique abaixo' : 'tudo certo'}
            accent={k.count_alertas > 0 ? 'text-amber-600' : 'text-emerald-600'}
          />
        </div>

        {/* Alerts */}
        {data.alerts?.length > 0 && (
          <Card data-testid="alerts-section">
            <CardContent className="pt-6 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <h2 className="font-bold">Alertas ({data.alerts.length})</h2>
              </div>
              {data.alerts.map((a) => <AlertCard key={a.id} alert={a} />)}
            </CardContent>
          </Card>
        )}

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Pie chart by area */}
          <Card className="lg:col-span-1">
            <CardContent className="pt-6">
              <h3 className="font-bold text-sm mb-3">Gastos por área</h3>
              {hasData ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      cx="50%" cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                    >
                      {pieData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v) => fmtBRL(v)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-60 flex items-center justify-center text-sm text-muted-foreground">
                  Sem gastos cadastrados ainda.
                </div>
              )}
              <div className="mt-3 space-y-1">
                {pieData.map((a) => (
                  <div key={a.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: a.color }} />
                      {a.name}
                    </span>
                    <span className="font-bold">{fmtBRL(a.value)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Monthly evolution bar chart */}
          <Card className="lg:col-span-2">
            <CardContent className="pt-6">
              <h3 className="font-bold text-sm mb-3">Evolução mensal de gastos</h3>
              {data.monthly_series?.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.monthly_series}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="mes"
                      tickFormatter={formatMonth}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                      formatter={(v) => fmtBRL(v)}
                      labelFormatter={formatMonth}
                    />
                    <Bar dataKey="total" fill="#16a34a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
                  Sem dados de gastos ainda.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Consumo por área detail */}
        <Card>
          <CardContent className="pt-6 space-y-3">
            <h3 className="font-bold text-sm">Consumo por área</h3>
            {data.by_area?.length > 0 ? (
              data.by_area.map((a) => {
                const pct = a.consumed_pct ?? 0;
                const barColor = pct > 100 ? 'bg-red-500' : pct > 85 ? 'bg-amber-500' : 'bg-emerald-500';
                return (
                  <div key={a.area_id} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: a.area_cor }} />
                        <span className="font-medium">{a.area_name}</span>
                        <span className="text-xs text-muted-foreground">{a.count} lanç.</span>
                      </span>
                      <span className="text-sm font-bold">
                        {fmtBRL(a.total_gasto)}
                        <span className="text-xs text-muted-foreground ml-2">
                          de {fmtBRL(a.valor_orcado)} ({fmtPct(pct)})
                        </span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground">Sem dados.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

// ---------------------------------------------------------------------------
// ANÁLISE — detailed drill-down view
// ---------------------------------------------------------------------------
export const FinanceiroAnalisePage = () => {
  const { data, loading } = useFinancialDashboard();

  if (loading || !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  // Build cumulative line: month vs spent
  const cumulative = [];
  let running = 0;
  for (const m of data.monthly_series || []) {
    running += m.total || 0;
    cumulative.push({ mes: m.mes, acumulado: running });
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="financeiro-analise-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <Activity className="h-6 w-6 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight">Análise Financeira</h1>
              <p className="text-muted-foreground mt-1">
                Visão detalhada por área, período, cidade e fornecedor.
              </p>
            </div>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/financeiro/dashboard">
              <LayoutDashboard className="h-3.5 w-3.5 mr-1.5" /> Dashboard executivo
            </Link>
          </Button>
        </div>

        {/* Alerts banner (concise) */}
        {data.alerts?.length > 0 && (
          <Card data-testid="alerts-banner">
            <CardContent className="pt-6 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <h2 className="font-bold">Alertas ativos ({data.alerts.length})</h2>
              </div>
              {data.alerts.map((a) => <AlertCard key={a.id} alert={a} />)}
            </CardContent>
          </Card>
        )}

        {/* Por área (tabela completa) */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-bold text-sm mb-3">Por área</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-xs uppercase text-muted-foreground">
                  <tr className="text-left">
                    <th className="py-2 px-2">Área</th>
                    <th className="py-2 px-2 text-right">Orçado</th>
                    <th className="py-2 px-2 text-right">Gasto</th>
                    <th className="py-2 px-2 text-right">Saldo</th>
                    <th className="py-2 px-2 text-right">% consumido</th>
                    <th className="py-2 px-2 text-right">Lanç.</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_area || []).map((a) => {
                    const saldo = (a.valor_orcado || 0) - (a.total_gasto || 0);
                    const pct = a.consumed_pct ?? 0;
                    const colorClass = pct > 100 ? 'text-red-600' : pct > 85 ? 'text-amber-600' : 'text-emerald-700';
                    return (
                      <tr key={a.area_id} className="border-b">
                        <td className="py-2 px-2">
                          <span className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full" style={{ background: a.area_cor }} />
                            {a.area_name}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right text-muted-foreground">{fmtBRL(a.valor_orcado)}</td>
                        <td className="py-2 px-2 text-right font-bold">{fmtBRL(a.total_gasto)}</td>
                        <td className="py-2 px-2 text-right">{fmtBRL(saldo)}</td>
                        <td className={`py-2 px-2 text-right font-bold ${colorClass}`}>{fmtPct(pct)}</td>
                        <td className="py-2 px-2 text-right text-muted-foreground">{a.count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Por período */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
              <CalendarDays className="h-4 w-4" /> Por período
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-xs uppercase text-muted-foreground">
                  <tr className="text-left">
                    <th className="py-2 px-2">Período</th>
                    <th className="py-2 px-2">Início</th>
                    <th className="py-2 px-2">Fim</th>
                    <th className="py-2 px-2 text-right">Orçado</th>
                    <th className="py-2 px-2 text-right">Gasto</th>
                    <th className="py-2 px-2 text-right">% consumido</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_period || []).map((p) => (
                    <tr key={p.periodo_id} className="border-b">
                      <td className="py-2 px-2 font-medium">{p.name}</td>
                      <td className="py-2 px-2 text-muted-foreground">{formatDateBR(p.data_inicio)}</td>
                      <td className="py-2 px-2 text-muted-foreground">{formatDateBR(p.data_fim)}</td>
                      <td className="py-2 px-2 text-right text-muted-foreground">{fmtBRL(p.valor_orcado)}</td>
                      <td className="py-2 px-2 text-right font-bold">{fmtBRL(p.total_gasto)}</td>
                      <td className="py-2 px-2 text-right">{fmtPct(p.consumed_pct || 0)}</td>
                    </tr>
                  ))}
                  {(data.by_period || []).length === 0 && (
                    <tr><td colSpan="6" className="py-4 text-center text-muted-foreground">
                      Nenhum período configurado no orçamento.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Por cidade */}
        {data.by_city_group?.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-bold text-sm mb-3">Por grupo de cidades</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-xs uppercase text-muted-foreground">
                    <tr className="text-left">
                      <th className="py-2 px-2">Grupo</th>
                      <th className="py-2 px-2 text-right">Orçado</th>
                      <th className="py-2 px-2 text-right">Gasto</th>
                      <th className="py-2 px-2 text-right">% consumido</th>
                      <th className="py-2 px-2 text-right">Lanç.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_city_group.map((g) => (
                      <tr key={g.city_group_id} className="border-b">
                        <td className="py-2 px-2 font-medium">{g.name}</td>
                        <td className="py-2 px-2 text-right text-muted-foreground">{fmtBRL(g.valor_orcado)}</td>
                        <td className="py-2 px-2 text-right font-bold">{fmtBRL(g.total_gasto)}</td>
                        <td className="py-2 px-2 text-right">{fmtPct(g.consumed_pct || 0)}</td>
                        <td className="py-2 px-2 text-right text-muted-foreground">{g.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top fornecedores */}
        {data.top_suppliers?.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Top fornecedores
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-xs uppercase text-muted-foreground">
                    <tr className="text-left">
                      <th className="py-2 px-2">Fornecedor</th>
                      <th className="py-2 px-2 text-right">Lanç.</th>
                      <th className="py-2 px-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_suppliers.map((s, idx) => (
                      <tr key={`${s.name}-${idx}`} className="border-b">
                        <td className="py-2 px-2 font-medium">{s.name}</td>
                        <td className="py-2 px-2 text-right text-muted-foreground">{s.count}</td>
                        <td className="py-2 px-2 text-right font-bold">{fmtBRL(s.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cumulative line chart */}
        {cumulative.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-bold text-sm mb-3">Evolução acumulada</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={cumulative}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="mes" tickFormatter={formatMonth} tick={{ fontSize: 11 }} />
                  <YAxis
                    tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    formatter={(v) => fmtBRL(v)}
                    labelFormatter={formatMonth}
                  />
                  <Line
                    type="monotone"
                    dataKey="acumulado"
                    stroke="#16a34a"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};
